import { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { apiClient } from '@/api/client'
import { MapPin, User, Phone, Building2, FileText, CheckCircle, AlertCircle, Calendar, Clock, Plane, Plus, X, ChevronUp, ChevronDown, ChevronRight, Car, Luggage, DollarSign, Loader2, HelpCircle, Flag, Baby } from 'lucide-react'
import { MonthDayPicker } from '@/components/ui/month-day-picker'
import { TimePicker } from '@/components/ui/time-picker'
import AddressAutocomplete from '@/components/address/AddressAutocomplete'
import { groupRegionsByCounty, sortByPriority, OTHER_COUNTY, type RegionOrder, EMPTY_REGION_ORDER } from '@/utils/taiwanRegions'

interface VehicleCategoryDetail {
  name: string
  image_url?: string | null
  description?: string | null
}

interface TenantBookingInfo {
  tenant_name: string
  vehicle_categories: string[]
  vehicle_categories_detail?: VehicleCategoryDetail[]
  payment_methods: string[]
  region_order?: RegionOrder
}

// 「其他」類別先隱藏（規劃好報價邏輯後再開放），送機/接機不受影響
const ORDER_CATEGORIES = ['送機', '接機']

const AIRPORTS = [
  '桃園機場一航廈',
  '桃園機場二航廈',
  '松山機場',
  '台中機場',
  '高雄機場',
  '基隆港',
]

const getTodayDate = () => {
  const today = new Date()
  return `${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getDate().toString().padStart(2, '0')}`
}

const formatToISO8601 = (dateStr: string, timeStr: string) => {
  const now = new Date()
  const [month, day] = dateStr.split('-')
  const [hours, minutes] = timeStr.split(':')
  // 訂車都是預約未來的行程：選的月日今年已經過了，就是指明年（例如 10 月選 2/4 → 明年 2/4）
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const candidate = new Date(now.getFullYear(), Number(month) - 1, Number(day))
  const year = candidate < today ? now.getFullYear() + 1 : now.getFullYear()
  return `${year}-${month}-${day}T${hours}:${minutes}:00`
}

const inputCls = (hasError?: boolean) =>
  `w-full border rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-300 bg-white ${hasError ? 'border-red-400' : 'border-gray-300'}`

/** 橫向可捲動的選項按鈕列（手機可左右滑動，也提供箭頭按鈕點擊捲動）*/
function ScrollableChips({
  options,
  value,
  onSelect,
  disabled,
}: {
  options: string[]
  value: string
  onSelect: (v: string) => void
  disabled?: boolean
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const scrollBy = (delta: number) => scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' })
  const handleScroll = () => setCanScrollLeft((scrollRef.current?.scrollLeft ?? 0) > 4)

  return (
    <div className="flex items-center gap-1">
      {options.length > 3 && canScrollLeft && (
        <button
          type="button"
          onClick={() => scrollBy(-140)}
          className="shrink-0 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
        </button>
      )}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex gap-2 overflow-x-auto py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {options.map(opt => (
          <button
            key={opt}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(opt)}
            className={`shrink-0 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
              value === opt ? 'bg-[#2563EB] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
      {options.length > 3 && (
        <button
          type="button"
          onClick={() => scrollBy(140)}
          className="shrink-0 rounded-full p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

/** 車型選擇：按鈕列，每個車型旁有個 help icon，hover（桌機）或點擊（手機）才顯示圖片+說明 */
function VehicleChips({
  options,
  value,
  onSelect,
}: {
  options: VehicleCategoryDetail[]
  value: string
  onSelect: (v: string) => void
}) {
  const [openInfo, setOpenInfo] = useState<string | null>(null)

  return (
    <div className="flex flex-wrap gap-2">
      {options.map(opt => {
        const hasInfo = !!(opt.image_url || opt.description)
        return (
          <div key={opt.name} className="relative">
            <button
              type="button"
              onClick={() => onSelect(opt.name)}
              className={`flex items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                value === opt.name ? 'bg-[#2563EB] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {opt.name}
              {hasInfo && (
                <span
                  onMouseEnter={() => setOpenInfo(opt.name)}
                  onMouseLeave={() => setOpenInfo(prev => (prev === opt.name ? null : prev))}
                  onClick={(e) => {
                    e.stopPropagation()
                    setOpenInfo(prev => (prev === opt.name ? null : opt.name))
                  }}
                  className={`-mr-1 rounded-full p-0.5 ${value === opt.name ? 'text-white/80 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <HelpCircle className="w-3.5 h-3.5" />
                </span>
              )}
            </button>

            {hasInfo && openInfo === opt.name && (
              <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 rounded-xl bg-white shadow-lg border border-gray-200 p-3 text-left">
                {opt.image_url && (
                  <img src={opt.image_url} alt={opt.name} className="w-full h-24 object-cover rounded-lg mb-2" />
                )}
                <p className="text-xs font-semibold text-gray-800 mb-0.5">{opt.name}</p>
                {opt.description && <p className="text-xs text-gray-500">{opt.description}</p>}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default function TenantBookingPageV2() {
  const { token } = useParams<{ token: string }>()
  const [info, setInfo] = useState<TenantBookingInfo | null>(null)
  const [loadError, setLoadError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [orderNumber, setOrderNumber] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [extraStops, setExtraStops] = useState<{ address: string; name: string; phone: string }[]>([])

  // Step 1：詢價
  const [quoteAirport, setQuoteAirport] = useState(AIRPORTS[0])
  const [quoteRegion, setQuoteRegion] = useState('')
  const [selectedCounty, setSelectedCounty] = useState('')
  const [regionOptions, setRegionOptions] = useState<string[]>([])
  const [loadingRegions, setLoadingRegions] = useState(false)
  const [quotePrice, setQuotePrice] = useState<number | null | undefined>(undefined) // undefined = 尚未查詢
  const [quoteBasePrice, setQuoteBasePrice] = useState<number | null>(null)
  const [quoteAddons, setQuoteAddons] = useState<{ label: string; amount: number }[]>([])
  const [loadingQuote, setLoadingQuote] = useState(false)
  const [showFullForm, setShowFullForm] = useState(false)

  const [form, setForm] = useState({
    company_name: '',
    tax_id: '',
    contact_person: '',
    contact_phone: '',
    order_category: '送機',
    vehicle_type: '',
    passenger_count: 1,
    luggage_large: 0,
    luggage_small: 0,
    pickup_date: getTodayDate(),
    pickup_time: '',
    pickup_location: '',
    dropoff_location: '',
    flight_number: '',
    flight_time: '',
    driver_notes: '',
    name_board: false,
    child_seat: 0,
    booster_seat: 0,
  })

  useEffect(() => {
    if (!token) return
    apiClient.get(`/public/tenant-booking/${token}`)
      .then(res => {
        setInfo(res.data)
        if (res.data.vehicle_categories?.length > 0) {
          setForm(f => ({ ...f, vehicle_type: res.data.vehicle_categories[0] }))
        }
      })
      .catch(() => setLoadError('此連結無效或已失效，請聯絡服務人員'))
  }, [token])

  const isAirportCategory = form.order_category === '送機' || form.order_category === '接機'

  // 選機場後抓區域清單
  useEffect(() => {
    if (!token || !isAirportCategory) return
    setLoadingRegions(true)
    setQuoteRegion('')
    setSelectedCounty('')
    setQuotePrice(undefined)
    apiClient.get(`/public/tenant-booking/${token}/regions`, { params: { airport: quoteAirport } })
      .then(res => setRegionOptions(res.data.regions || []))
      .catch(() => setRegionOptions([]))
      .finally(() => setLoadingRegions(false))
  }, [token, quoteAirport, isAirportCategory])

  // 選了機場+區域後查價格
  useEffect(() => {
    if (!token || !isAirportCategory || !quoteRegion) {
      setQuotePrice(undefined)
      setQuoteBasePrice(null)
      setQuoteAddons([])
      return
    }
    setLoadingQuote(true)
    apiClient.get(`/public/tenant-booking/${token}/quote`, {
      params: {
        airport: quoteAirport, region: quoteRegion, vehicle_type: form.vehicle_type || undefined, category: form.order_category,
        extra_passenger_count: extraStops.filter(s => s.address.trim()).length,
        name_board: form.name_board,
        child_seat_count: form.child_seat,
        booster_seat_count: form.booster_seat,
      },
    })
      .then(res => {
        setQuotePrice(res.data.price)
        setQuoteBasePrice(res.data.base_price ?? null)
        setQuoteAddons(res.data.addons || [])
      })
      .catch(() => {
        setQuotePrice(null)
        setQuoteBasePrice(null)
        setQuoteAddons([])
      })
      .finally(() => setLoadingQuote(false))
  }, [token, quoteAirport, quoteRegion, form.vehicle_type, form.order_category, isAirportCategory, extraStops, form.name_board, form.child_seat, form.booster_seat])

  const set = (field: string, value: string | number | boolean) =>
    setForm(f => ({ ...f, [field]: value }))

  const handleContinue = () => {
    // 機場自動帶入正確欄位；區域側留給客戶自己填詳細地址
    if (form.order_category === '送機') {
      set('dropoff_location', quoteAirport)
    } else if (form.order_category === '接機') {
      set('pickup_location', quoteAirport)
    }
    setShowFullForm(true)
  }

  const getValidationErrors = () => {
    const e: Record<string, string> = {}
    if (!form.contact_person.trim()) e.contact_person = '請填寫聯絡人'
    if (!form.contact_phone.trim()) e.contact_phone = '請填寫聯絡電話'
    if (!form.pickup_date) e.pickup_date = '請選擇日期'
    if (!form.pickup_time) e.pickup_time = '請選擇時間'
    if (!form.pickup_location.trim()) e.pickup_location = '請填寫上車地點'
    if (!form.dropoff_location.trim()) e.dropoff_location = '請填寫下車地點'
    if (!form.vehicle_type) e.vehicle_type = '請選擇車型'
    return e
  }

  const validate = () => {
    const e = getValidationErrors()
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const isFormComplete = Object.keys(getValidationErrors()).length === 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    try {
      const payload = {
        company_name: form.company_name || undefined,
        tax_id: form.tax_id || undefined,
        contact_person: form.contact_person,
        contact_phone: form.contact_phone,
        order_category: form.order_category,
        vehicle_type: form.vehicle_type,
        passenger_count: Number(form.passenger_count),
        pickup_datetime: formatToISO8601(form.pickup_date, form.pickup_time),
        pickup_location: form.pickup_location,
        dropoff_location: form.dropoff_location,
        additional_stops: extraStops.filter(s => s.address.trim()).map((s, i) => ({
          address: s.address, order: i + 1,
          name: s.name.trim() || undefined,
          phone: s.phone.trim() || undefined,
        })),
        driver_notes: [
          (Number(form.luggage_large) > 0 ? `大行李 ${form.luggage_large} 件` : ''),
          (Number(form.luggage_small) > 0 ? `小行李 ${form.luggage_small} 件` : ''),
          form.driver_notes,
        ].filter(Boolean).join('、') || undefined,
        flight_number: isAirportCategory && form.flight_number ? form.flight_number : undefined,
        arrival_time: isAirportCategory && form.flight_time ? form.flight_time : undefined,
        // 報價僅供後端比對用，實際金額一律由後端重新查價鎖定，不信任前端數字
        airport: isAirportCategory ? quoteAirport : undefined,
        region: isAirportCategory ? quoteRegion : undefined,
        name_board: form.name_board,
        child_seat: Number(form.child_seat),
        booster_seat: Number(form.booster_seat),
      }
      const res = await apiClient.post(`/public/tenant-booking/${token}/v2`, payload)
      setOrderNumber(res.data.order_number)
      setSubmitted(true)
    } catch (err: any) {
      setErrors({ submit: err?.response?.data?.detail || '預約失敗，請稍後再試' })
    } finally {
      setSubmitting(false)
    }
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-sm text-center">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-gray-600 text-sm">{loadError}</p>
        </div>
      </div>
    )
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-sm text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="font-bold text-gray-800 mb-1">預約已送出</p>
          <p className="text-sm text-gray-500 mb-1">我們將盡快與您確認行程</p>
          <p className="text-xs text-gray-400 font-mono">#{orderNumber}</p>
        </div>
      </div>
    )
  }

  const pickupIsAirport = form.order_category === '接機'
  const dropoffIsAirport = form.order_category === '送機'
  // 「其他」類別沒有詢價，直接視同已「繼續」
  const readyForFullForm = showFullForm || form.order_category === '其他'

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
          <p className="text-xs text-gray-400 mb-1">{info.tenant_name}</p>
          <h1 className="text-lg font-bold text-gray-800">線上詢價 ・ 預約</h1>
        </div>

        {/* 接送類別 */}
        <div className="bg-white rounded-2xl shadow-sm p-5 mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1.5">接送類別</label>
          <div className="flex bg-gray-100 p-1 rounded-lg">
            {ORDER_CATEGORIES.map(cat => (
              <button
                key={cat}
                type="button"
                onClick={() => {
                  set('order_category', cat)
                  set('pickup_location', '')
                  set('dropoff_location', '')
                  setExtraStops([])
                  setShowFullForm(false)
                  setQuoteRegion('')
                  setSelectedCounty('')
                  setQuotePrice(undefined)
                }}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                  form.order_category === cat ? 'bg-[#2563EB] text-white' : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Step 1：詢價（僅送機/接機） */}
        {isAirportCategory && !showFullForm && (
          <div className="bg-white rounded-2xl shadow-sm p-5 mb-4 space-y-4">
            <p className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
              <DollarSign className="w-4 h-4" />線上詢價
            </p>

            <div>
              <label className="block text-xs text-gray-600 mb-1">
                {form.order_category === '接機' ? '上車地點' : '下車地點'}
              </label>
              <ScrollableChips options={AIRPORTS} value={quoteAirport} onSelect={setQuoteAirport} />
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">
                {form.order_category === '接機' ? '下車區域' : '上車區域'}
              </label>
              {loadingRegions ? (
                <p className="text-xs text-gray-400 py-2">載入中...</p>
              ) : regionOptions.length === 0 ? (
                <p className="text-xs text-gray-400 py-2">此機場目前尚無可線上詢價的區域，請直接填寫預約，我們會盡快為您報價</p>
              ) : (() => {
                const regionGroups = groupRegionsByCounty(regionOptions)
                // 資料量少或反解析不出縣市分組時，直接用單層清單就好，不用多一次點擊
                if (regionGroups.size <= 1) {
                  return <ScrollableChips options={regionOptions} value={quoteRegion} onSelect={setQuoteRegion} />
                }
                const regionOrder = info.region_order || EMPTY_REGION_ORDER
                const countiesRaw = [...regionGroups.keys()].sort((a, b) => a === OTHER_COUNTY ? 1 : b === OTHER_COUNTY ? -1 : 0)
                const counties = sortByPriority(countiesRaw, regionOrder.counties)
                const districtObjs = sortByPriority(
                  regionGroups.get(selectedCounty) || [],
                  regionOrder.districts[selectedCounty] || [],
                  (d) => d.district,
                )
                return (
                  <div className="space-y-2">
                    <ScrollableChips
                      options={counties}
                      value={selectedCounty}
                      onSelect={(c) => { setSelectedCounty(c); setQuoteRegion('') }}
                    />
                    {selectedCounty && (
                      <ScrollableChips
                        options={districtObjs.map(d => d.district)}
                        value={districtObjs.find(d => d.region === quoteRegion)?.district || ''}
                        onSelect={(district) => {
                          const match = districtObjs.find(d => d.district === district)
                          if (match) setQuoteRegion(match.region)
                        }}
                      />
                    )}
                  </div>
                )
              })()}
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">車型</label>
              <VehicleChips
                options={info.vehicle_categories_detail?.length ? info.vehicle_categories_detail : info.vehicle_categories.map(name => ({ name }))}
                value={form.vehicle_type}
                onSelect={v => set('vehicle_type', v)}
              />
            </div>

            {quoteRegion && (
              <div className="rounded-xl bg-blue-50 p-4 text-center">
                {loadingQuote ? (
                  <Loader2 className="w-5 h-5 text-blue-400 animate-spin mx-auto" />
                ) : quotePrice != null ? (
                  <>
                    <p className="text-xs text-blue-600 mb-1">預估車資</p>
                    <p className="text-2xl font-bold text-blue-700">${quotePrice}</p>
                  </>
                ) : (
                  <p className="text-sm font-medium text-red-800 bg-red-100 rounded-lg px-3 py-2">此區域尚無公開牌價，請直接送出預約，我們會盡快為您報價</p>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={handleContinue}
              disabled={!quoteRegion}
              className="w-full bg-[#2563EB] hover:bg-blue-700 text-white font-medium py-2.5 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              繼續預約
            </button>

            {(regionOptions.length === 0 || quotePrice === null) && !loadingRegions && !loadingQuote && (
              <button
                type="button"
                onClick={handleContinue}
                className="w-full text-center text-xs text-gray-400 hover:text-gray-600 underline"
              >
                跳過詢價，直接送出預約詢問
              </button>
            )}
          </div>
        )}

        {/* Step 2：完整表單 */}
        {readyForFullForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-5 space-y-4">
          {isAirportCategory && quotePrice != null && (
            <div className="rounded-xl bg-red-100 p-3 text-sm space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-red-800 font-medium">{quoteAirport} ・ {quoteRegion} ・ {form.vehicle_type}</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-red-800">${quoteBasePrice ?? quotePrice}</span>
                  <button
                    type="button"
                    onClick={() => setShowFullForm(false)}
                    className="text-xs text-red-600 hover:text-red-800 underline"
                  >
                    重新選擇
                  </button>
                </div>
              </div>
              {quoteAddons.map(a => (
                <div key={a.label} className="flex items-center justify-between text-red-700">
                  <span>{a.label}</span>
                  <span>${a.amount}</span>
                </div>
              ))}
              {quoteAddons.length > 0 && (
                <div className="flex items-center justify-between font-bold text-red-800 border-t border-red-200 pt-1 mt-1">
                  <span>總計</span>
                  <span>${quotePrice}</span>
                </div>
              )}
            </div>
          )}

          {/* 航班資訊（送機/接機） */}
          {isAirportCategory && (
            <div className="bg-blue-50 rounded-xl p-4 space-y-3">
              <p className="text-sm font-medium text-blue-700">
                <Plane className="w-4 h-4 inline mr-1" />航班資訊（選填）
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">航班編號</label>
                  <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-300"
                    value={form.flight_number} onChange={e => set('flight_number', e.target.value)} placeholder="例：CI100" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">
                    {form.order_category === '接機' ? '抵達時間' : '起飛時間'}
                  </label>
                  <TimePicker value={form.flight_time} onChange={v => set('flight_time', v)} placeholder="選擇時間" />
                </div>
              </div>
            </div>
          )}

          {/* 乘車日期 | 乘車時間 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Calendar className="w-4 h-4 inline mr-1" />乘車日期 <span className="text-red-500">*</span>
              </label>
              <MonthDayPicker value={form.pickup_date} onChange={v => set('pickup_date', v)} placeholder="選擇日期" />
              {errors.pickup_date && <p className="text-red-500 text-xs mt-1">{errors.pickup_date}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Clock className="w-4 h-4 inline mr-1" />乘車時間 <span className="text-red-500">*</span>
              </label>
              <TimePicker value={form.pickup_time} onChange={v => set('pickup_time', v)} placeholder="選擇時間" />
              {errors.pickup_time && <p className="text-red-500 text-xs mt-1">{errors.pickup_time}</p>}
            </div>
          </div>

          {/* 公司名稱 | 統編（選填）*/}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Building2 className="w-4 h-4 inline mr-1" />公司名稱（選填）
              </label>
              <input className={inputCls()} value={form.company_name}
                onChange={e => set('company_name', e.target.value)} placeholder="公司名稱" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">統編（選填）</label>
              <input className={inputCls()} value={form.tax_id}
                onChange={e => set('tax_id', e.target.value)} placeholder="統一編號" />
            </div>
          </div>

          {/* 聯絡人 | 電話 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <User className="w-4 h-4 inline mr-1" />聯絡人 <span className="text-red-500">*</span>
              </label>
              <input className={inputCls(!!errors.contact_person)} value={form.contact_person}
                onChange={e => set('contact_person', e.target.value)} placeholder="聯絡人姓名" />
              {errors.contact_person && <p className="text-red-500 text-xs mt-1">{errors.contact_person}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Phone className="w-4 h-4 inline mr-1" />聯絡電話 <span className="text-red-500">*</span>
              </label>
              <input className={inputCls(!!errors.contact_phone)} value={form.contact_phone}
                onChange={e => set('contact_phone', e.target.value)} placeholder="0912-345-678" />
              {errors.contact_phone && <p className="text-red-500 text-xs mt-1">{errors.contact_phone}</p>}
            </div>
          </div>

          {/* 上車地點 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <MapPin className="w-4 h-4 inline mr-1" />上車地點 <span className="text-red-500">*</span>
            </label>
            {pickupIsAirport ? (
              <select value={form.pickup_location} onChange={e => set('pickup_location', e.target.value)}
                className={inputCls(!!errors.pickup_location)}>
                <option value="">選擇機場</option>
                {AIRPORTS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            ) : (
              <AddressAutocomplete
                placeholder={quoteRegion ? `請輸入${quoteRegion}詳細地址` : '請輸入上車地址'}
                value={form.pickup_location}
                onChange={v => set('pickup_location', v)}
                className={errors.pickup_location ? 'border-red-400' : ''} />
            )}
            {errors.pickup_location && <p className="text-red-500 text-xs mt-1">{errors.pickup_location}</p>}
          </div>

          {/* 附加乘客（中途停靠點，可各自填姓名/電話，選填） */}
          {extraStops.map((stop, idx) => (
            <div key={idx} className="flex gap-2 items-start bg-gray-50 rounded-xl p-3">
              <div className="flex-1 space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  <MapPin className="w-4 h-4 inline mr-1" />附加乘客 {extraStops.length > 1 ? idx + 1 : ''}
                </label>
                <AddressAutocomplete
                  value={stop.address}
                  onChange={v => {
                    const updated = [...extraStops]
                    updated[idx] = { ...updated[idx], address: v }
                    setExtraStops(updated)
                  }}
                  placeholder="請輸入地址"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input className={inputCls()} value={stop.name}
                    onChange={e => {
                      const updated = [...extraStops]
                      updated[idx] = { ...updated[idx], name: e.target.value }
                      setExtraStops(updated)
                    }}
                    placeholder="姓名（選填）" />
                  <input className={inputCls()} value={stop.phone}
                    onChange={e => {
                      const updated = [...extraStops]
                      updated[idx] = { ...updated[idx], phone: e.target.value }
                      setExtraStops(updated)
                    }}
                    placeholder="電話（選填）" />
                </div>
              </div>
              <div className="flex flex-col gap-1 mt-6 shrink-0">
                <button type="button" disabled={idx === 0}
                  onClick={() => {
                    const updated = [...extraStops]
                    ;[updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]]
                    setExtraStops(updated)
                  }}
                  className="p-1.5 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  <ChevronUp className="w-3.5 h-3.5" />
                </button>
                <button type="button" disabled={idx === extraStops.length - 1}
                  onClick={() => {
                    const updated = [...extraStops]
                    ;[updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]]
                    setExtraStops(updated)
                  }}
                  className="p-1.5 rounded-md bg-gray-100 text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => setExtraStops(extraStops.filter((_, i) => i !== idx))}
                  className="p-1.5 rounded-md bg-red-50 text-red-400 hover:bg-red-100 transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}

          <button type="button" onClick={() => setExtraStops([...extraStops, { address: '', name: '', phone: '' }])}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-blue-300 text-blue-500 text-sm hover:bg-blue-50 transition-colors">
            <Plus className="w-4 h-4" />新增附加乘客
          </button>

          {/* 下車地點 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <MapPin className="w-4 h-4 inline mr-1" />下車地點 <span className="text-red-500">*</span>
            </label>
            {dropoffIsAirport ? (
              <select value={form.dropoff_location} onChange={e => set('dropoff_location', e.target.value)}
                className={inputCls(!!errors.dropoff_location)}>
                <option value="">選擇機場</option>
                {AIRPORTS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            ) : (
              <AddressAutocomplete
                placeholder={quoteRegion ? `請輸入${quoteRegion}詳細地址` : '請輸入下車地址'}
                value={form.dropoff_location}
                onChange={v => set('dropoff_location', v)}
                className={errors.dropoff_location ? 'border-red-400' : ''} />
            )}
            {errors.dropoff_location && <p className="text-red-500 text-xs mt-1">{errors.dropoff_location}</p>}
          </div>

          {/* 車型（送機/接機已在詢價步驟選過，這裡只給「其他」類別選） | 人數 | 行李大 | 行李小 */}
          <div className={`grid gap-3 ${isAirportCategory ? 'grid-cols-3' : 'grid-cols-4'}`}>
            {!isAirportCategory && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <Car className="w-4 h-4 inline mr-1" />車型 <span className="text-red-500">*</span>
                </label>
                <select value={form.vehicle_type} onChange={e => set('vehicle_type', e.target.value)}
                  className={inputCls(!!errors.vehicle_type)}>
                  {info.vehicle_categories.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                {errors.vehicle_type && <p className="text-red-500 text-xs mt-1">{errors.vehicle_type}</p>}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <User className="w-4 h-4 inline mr-1" />人數
              </label>
              <input className={inputCls()} type="number" min={1} max={20} value={form.passenger_count}
                onChange={e => set('passenger_count', Number(e.target.value))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Luggage className="w-4 h-4 inline mr-1" />行李大
              </label>
              <input className={inputCls()} type="number" min={0} max={20} value={form.luggage_large}
                onChange={e => set('luggage_large', Number(e.target.value))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Luggage className="w-4 h-4 inline mr-1" />行李小
              </label>
              <input className={inputCls()} type="number" min={0} max={20} value={form.luggage_small}
                onChange={e => set('luggage_small', Number(e.target.value))} />
            </div>
          </div>

          {/* 舉牌 | 兒童座椅 | 增高墊（加價金額由車行後台設定） */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Flag className="w-4 h-4 inline mr-1" />舉牌
              </label>
              <button
                type="button"
                onClick={() => set('name_board', !form.name_board)}
                className={`w-full py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                  form.name_board ? 'bg-[#2563EB] text-white border-[#2563EB]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {form.name_board ? '需要' : '不需要'}
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Baby className="w-4 h-4 inline mr-1" />兒童座椅
              </label>
              <input className={inputCls()} type="number" min={0} max={10} value={form.child_seat}
                onChange={e => set('child_seat', Number(e.target.value))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">增高墊</label>
              <input className={inputCls()} type="number" min={0} max={10} value={form.booster_seat}
                onChange={e => set('booster_seat', Number(e.target.value))} />
            </div>
          </div>

          {/* 備註 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              <FileText className="w-4 h-4 inline mr-1" />備註
            </label>
            <textarea value={form.driver_notes} onChange={e => set('driver_notes', e.target.value)}
              placeholder="其他需求或注意事項..." rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-300 resize-none" />
          </div>

          {errors.submit && <p className="text-red-500 text-sm">{errors.submit}</p>}

          <button type="submit" disabled={submitting || !isFormComplete}
            className="w-full bg-[#2563EB] hover:bg-blue-700 text-white font-medium py-2.5 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            {submitting ? '送出中...' : (isAirportCategory && quotePrice != null ? `送出預約 (總金額:${quotePrice})` : '送出預約')}
          </button>
        </form>
        )}
      </div>
    </div>
  )
}
